import {type ChangeEvent, createRef, type RefObject} from 'react';

import {action, makeObservable} from 'mobx';

import type {Nullable} from '../utility-types';
import type {Field, ValidationRule} from './types';

import {createField} from './createField';

export interface HtmlTextInputField<T> extends Field<string> {
  onChange: (event: ChangeEvent<T>) => void;
  ref: RefObject<Nullable<T>>;
}

export const createHtmlInputField = <T = HTMLInputElement>(
  name: string,
  {
    customValidate,
    defaultValue = '',
    extraFields = {},
    validationRule,
  }: {
    customValidate?: (value: string) => boolean;
    defaultValue?: string;
    extraFields?: {[key: string]: any};
    validationRule?: ValidationRule;
  } = {},
) => {
  const formField = createField<string>(name, {
    customValidate,
    defaultValue,
    extraFields,
    validationRule,
  }) as HtmlTextInputField<T>;

  formField.ref = createRef();

  formField.value = defaultValue;

  formField.onChange = action((e: ChangeEvent<T>) => {
    if (
      e.currentTarget instanceof HTMLInputElement ||
      e.currentTarget instanceof HTMLTextAreaElement ||
      e.currentTarget instanceof HTMLSelectElement
    ) {
      formField.value = e.currentTarget.value;

      formField.validate();
    }
  });

  return makeObservable(formField, {
    onChange: action.bound,
  });
};
